import { useState, useMemo, useEffect } from 'react';
import { Bot, SearchFilters } from '@/types/Bot';
import { supabase } from '@/integrations/supabase/client';

export const useSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    category: 'All Categories',
    price: [],
    minRating: 0,
    sortBy: 'relevance',
    modelType: [],
    loginRequired: 'all',
    languageSupported: [],
    useCase: []
  });

  const [remoteBots, setRemoteBots] = useState<Bot[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchBots = async () => {
      try {
        const { data, error } = await supabase
          .from('bots')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(200);
        if (error) {
          console.error('Failed to load bots from Supabase:', error);
          return;
        }
        const mapped: Bot[] = (data || []).map((b: any) => ({
          id: b.id,
          name: b.name,
          description: b.description || '',
          category: b.use_case || 'General',
          rating: Number(b.rating ?? 4.5),
          reviewCount: Number(b.conversations ?? 0),
          price: (b.pricing || 'Free'),
          features: (b.key_features || b.features_array || []) as string[],
          tags: (b.tags || []) as string[],
          popularity: Number(b.fit_index ?? 50),
          imageUrl: b.thumbnail || '/api/placeholder/400/300',
          website: b.website_url || b.site_url || '',
          modelType: b.model_type || undefined,
          loginRequired: Boolean(b.login_required),
          preview_url: b.preview_url ?? null,
          site_url: b.site_url ?? null,
          thumbnail: b.thumbnail ?? null,
          key_features: (b.key_features || []) as string[],
          subcategory: b.subcategory || undefined,
          best_use_cases: (b.best_use_cases || []) as string[],
          fit_index: Number(b.fit_index ?? 50),
        }));
        if (!cancelled) setRemoteBots(mapped);
      } catch (e) {
        console.error('Error fetching bots:', e);
      }
    };
    fetchBots();
    return () => { cancelled = true; };
  }, []);

  const filteredBots = useMemo(() => {
    let results = remoteBots;

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      results = results.filter(bot =>
        bot.name.toLowerCase().includes(term) ||
        bot.description.toLowerCase().includes(term) ||
        bot.category.toLowerCase().includes(term) ||
        bot.tags.some(tag => tag.toLowerCase().includes(term)) ||
        bot.features.some(feature => feature.toLowerCase().includes(term))
      );
    }

    // Filter by category
    if (filters.category !== 'All Categories') {
      results = results.filter(bot => bot.category === filters.category);
    }

    // Filter by price
    if (filters.price.length > 0) {
      results = results.filter(bot => filters.price.includes(bot.price));
    }

    // Filter by rating
    if (filters.minRating > 0) {
      results = results.filter(bot => bot.rating >= filters.minRating);
    }

    // Sort results
    results.sort((a, b) => {
      switch (filters.sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'popularity':
          return b.popularity - a.popularity;
        case 'newest':
          return b.id.localeCompare(a.id);
        case 'relevance':
        default:
          // Simple relevance score based on search term matches
          if (!searchTerm.trim()) return b.popularity - a.popularity;
          
          const getRelevanceScore = (bot: Bot) => {
            const term = searchTerm.toLowerCase();
            let score = 0;
            
            if (bot.name.toLowerCase().includes(term)) score += 10;
            if (bot.category.toLowerCase().includes(term)) score += 8;
            if (bot.description.toLowerCase().includes(term)) score += 5;
            bot.tags.forEach(tag => {
              if (tag.toLowerCase().includes(term)) score += 3;
            });
            bot.features.forEach(feature => {
              if (feature.toLowerCase().includes(term)) score += 2;
            });
            
            return score + (bot.popularity / 100) + bot.rating;
          };
          
          return getRelevanceScore(b) - getRelevanceScore(a);
      }
    });

    return results;
  }, [searchTerm, filters, remoteBots]);

  return {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    filteredBots
  };
};